import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import MaterialIcons from "../icons/MaterialIcons";
import PageNavigator from "../classes/PageNavigator";
import IconButton from "./IconButton";
import RequestsByInvoiceRequest from "../models/RequestsByInvoiceRequest";
import PaymentStatus from "../classes/PaymentStatus";
import PaginationView from "./PaginationView";
import Query from "../classes/Query";
import NOKUser from "../models/NOKUser";
import MessageDispatch from "../rocket/MessageDispatch";
import Button from "./Button";
import RequestsByInvoice from "../classes/RequestsByInvoice";
import ListWrapper from "../utils/ListWrapper";
import Browser from "../classes/Browser";
import Administrator from "../models/Administrator";
import Invoice from "../models/Invoice";
import TextView from "./TextView";
import SearchRequest from "../models/SearchRequest";
import DFile from "../classes/DFile";
import RPCServices from "../rpc/RPCServices";
import CollectionUtils from "../utils/CollectionUtils";
import DateUtils from "../classes/DateUtils";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _PrintButtonOnPressed = (d3eState: BillingDetailsViewRefs) => void;

type _DownloadButtonOnPressed = (d3eState: BillingDetailsViewRefs) => void;

type _PaymentButtonOnPressed = (d3eState: BillingDetailsViewRefs) => void;

export interface BillingDetailsViewProps extends BaseUIProps {
  key?: string;
  obj: Invoice;
  admin: Administrator;
  user: NOKUser;
}
/// To store state data for BillingDetailsView
class BillingDetailsViewRefs {
  public downloadButton: DownloadButtonState = new DownloadButtonState();
  public paymentButton: PaymentButtonState = new PaymentButtonState();
  public printButton: PrintButtonState = new PrintButtonState();
}

interface PaymentButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BillingDetailsViewRefs;
  _onPaymentButtonHandler?: _PaymentButtonOnPressed;
}

class PaymentButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _PaymentButtonWithState extends ObservableComponent<PaymentButtonWithStateProps> {
  paymentButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: PaymentButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get paymentButton(): PaymentButtonState {
    return this.props.d3eState.paymentButton;
  }
  public get d3eState(): BillingDetailsViewRefs {
    return this.props.d3eState;
  }
  public get _onPaymentButtonHandler(): _PaymentButtonOnPressed {
    return this.props._onPaymentButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("paymentButton", null, this.paymentButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["paymentButton", "paymentButton.", "paymentButton.hover"],
      this.rebuild
    );
  }
  public paymentButtonOnEnter(event): void {
    return this.paymentButton.setHover(true);
  }
  public paymentButtonOnExit(event): void {
    return this.paymentButton.setHover(false);
  }
  public dispose(): void {
    this.paymentButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      child: Button({
        padding: this.paymentButton.hover
          ? cStyle.tButtonSecondaryPaddingOnHover
          : cStyle.tButtonSecondaryPaddingOn,
        decoration: this.paymentButton.hover
          ? cStyle.tButtonSecondaryDecorationOnHover
          : cStyle.tButtonSecondaryDecorationOn,
        disable: this.paymentButton.disable,
        onPressed: () => {
          this._onPaymentButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Make Payment" }),
        onEnter: (event) => {
          this.paymentButtonOnEnter(event);
        },
        onExit: (event) => {
          this.paymentButtonOnExit(event);
        },
      }),
      className: "x248",
    });
  }
}
function PaymentButtonWithState(props: PaymentButtonWithStateProps) {
  return React.createElement(_PaymentButtonWithState, props);
}

interface DownloadButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BillingDetailsViewRefs;
  _onDownloadButtonHandler?: _DownloadButtonOnPressed;
}

class DownloadButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _DownloadButtonWithState extends ObservableComponent<DownloadButtonWithStateProps> {
  downloadButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: DownloadButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get downloadButton(): DownloadButtonState {
    return this.props.d3eState.downloadButton;
  }
  public get d3eState(): BillingDetailsViewRefs {
    return this.props.d3eState;
  }
  public get _onDownloadButtonHandler(): _DownloadButtonOnPressed {
    return this.props._onDownloadButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("downloadButton", null, this.downloadButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["downloadButton", "downloadButton.", "downloadButton.hover"],
      this.rebuild
    );
  }
  public downloadButtonOnEnter(event): void {
    return this.downloadButton.setHover(true);
  }
  public downloadButtonOnExit(event): void {
    return this.downloadButton.setHover(false);
  }
  public dispose(): void {
    this.downloadButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      width: 100,
      child: Button({
        padding: this.downloadButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.downloadButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.downloadButton.disable,
        onPressed: () => {
          this._onDownloadButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Download" }),
        onEnter: (event) => {
          this.downloadButtonOnEnter(event);
        },
        onExit: (event) => {
          this.downloadButtonOnExit(event);
        },
      }),
      className: "x783 hc",
    });
  }
}
function DownloadButtonWithState(props: DownloadButtonWithStateProps) {
  return React.createElement(_DownloadButtonWithState, props);
}

interface PrintButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BillingDetailsViewRefs;
  _onPrintButtonHandler?: _PrintButtonOnPressed;
}

class PrintButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _PrintButtonWithState extends ObservableComponent<PrintButtonWithStateProps> {
  printButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: PrintButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get printButton(): PrintButtonState {
    return this.props.d3eState.printButton;
  }
  public get d3eState(): BillingDetailsViewRefs {
    return this.props.d3eState;
  }
  public get _onPrintButtonHandler(): _PrintButtonOnPressed {
    return this.props._onPrintButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("printButton", null, this.printButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["printButton", "printButton.", "printButton.hover"], this.rebuild);
  }
  public printButtonOnEnter(event): void {
    return this.printButton.setHover(true);
  }
  public printButtonOnExit(event): void {
    return this.printButton.setHover(false);
  }
  public dispose(): void {
    this.printButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      width: 100,
      child: Button({
        padding: this.printButton.hover
          ? cStyle.tButtonSecondaryPaddingOnHover
          : cStyle.tButtonSecondaryPaddingOn,
        decoration: this.printButton.hover
          ? cStyle.tButtonSecondaryDecorationOnHover
          : cStyle.tButtonSecondaryDecorationOn,
        disable: this.printButton.disable,
        onPressed: () => {
          this._onPrintButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Print" }),
        onEnter: (event) => {
          this.printButtonOnEnter(event);
        },
        onExit: (event) => {
          this.printButtonOnExit(event);
        },
      }),
      className: "x43a hc",
    });
  }
}
function PrintButtonWithState(props: PrintButtonWithStateProps) {
  return React.createElement(_PrintButtonWithState, props);
}

class _BillingDetailsViewState extends ObservableComponent<BillingDetailsViewProps> {
  static defaultProps = { obj: null, admin: null, user: null };
  d3eState: BillingDetailsViewRefs = new BillingDetailsViewRefs();
  query: RequestsByInvoice = null;
  request: RequestsByInvoiceRequest = null;
  requestListByInvoice: Array<SearchRequest> = ListWrapper.widget(
    this,
    "requestListByInvoice"
  );
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BillingDetailsViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): Invoice {
    return this.props.obj;
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.updateSyncProperty("admin", this.props.admin);

    this.updateSyncProperty("user", this.props.user);

    this.on(
      [
        "request",
        "request.ascending",
        "request.hospital",
        "request.invoice",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.on(["query", "query.items"], this.computeRequestListByInvoice);

    this.computeRequestListByInvoice();

    this.on(
      [
        "obj",
        "obj.amount",
        "obj.dueDate",
        "obj.hospital",
        "obj.hospital.name",
        "obj.invoiceDate",
        "obj.requestCount",
        "obj.status",
        "obj.title",
        "query",
        "query.count",
        "query.items",
        "request",
        "request.offset",
        "request.pageSize",
        "requestListByInvoice",
        "requestListByInvoice.createdBy",
        "requestListByInvoice.createdBy.name",
        "requestListByInvoice.requestDate",
        "requestListByInvoice.searchId",
        "user",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: BillingDetailsViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }
  }
  public setQuery(val: RequestsByInvoice): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    MessageDispatch.get().dispose(this.query);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getRequestsByInvoice(
          UsageConstants.QUERY_GETREQUESTSBYINVOICE_BILLINGDETAILSVIEW_PROPERTIES_QUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setRequest(val: RequestsByInvoiceRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setRequestListByInvoice(val: Array<SearchRequest>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.requestListByInvoice,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl(
      "requestListByInvoice",
      this.requestListByInvoice,
      val
    );

    this.requestListByInvoice.clear();

    this.requestListByInvoice.addAll(val);

    this.fire("requestListByInvoice", this);
  }
  public addToRequestListByInvoice(
    val: SearchRequest,
    index: number = -1
  ): void {
    if (index === -1) {
      if (!this.requestListByInvoice.contains(val))
        this.requestListByInvoice.add(val);
    } else {
      this.requestListByInvoice.remove(
        this.requestListByInvoice.elementAt(index)
      );

      this.requestListByInvoice.add(val);
    }

    this.fire("requestListByInvoice", this, val, true);

    this.updateObservable("requestListByInvoice", null, val);
  }
  public removeFromRequestListByInvoice(val: SearchRequest): void {
    this.requestListByInvoice.remove(val);

    this.fire("requestListByInvoice", this, val, false);

    this.removeObservable("requestListByInvoice", val);
  }
  public computeRequestListByInvoice = (): void => {
    try {
      this.setRequestListByInvoice(Array.from(this.query?.items ?? []));
    } catch (exception) {
      console.log(
        " exception in computeRequestListByInvoice : " + exception.toString()
      );

      this.setRequestListByInvoice([]);
    }
  };
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          children: [
            ui.Row({
              children: [
                ui.Container({
                  padding: ui.EdgeInsets.fromLTRB(
                    0.0,
                    0.0,
                    15.0,
                    8.0,
                    new Map()
                  ),
                  child: IconButton({
                    size: 24,
                    icon: MaterialIcons.arrow_back,
                    onPressed: () => {
                      this.onBackButtonHandler(this.d3eState);
                    },
                  }),
                  key: "0",
                  className: "x9b",
                }),
                TextView({
                  data: "Billing Details View",
                  style: new ui.TextStyle({
                    fontSize: cStyle.tTextViewHeadlineThreeFontSizeOn,
                    fontWeight: cStyle.tTextViewHeadlineThreeFontWeightOn,
                  }),
                  className: "xba5",
                  key: "1",
                }),
              ],
              key: "0",
            }),
            this.user !== null
              ? ui.Row({
                  children: [
                    PrintButtonWithState({
                      d3eState: this.d3eState,
                      _onPrintButtonHandler: this.onPrintButtonHandler,
                      key: "0",
                    }),
                    DownloadButtonWithState({
                      d3eState: this.d3eState,
                      _onDownloadButtonHandler: this.onDownloadButtonHandler,
                      key: "1",
                    }),
                    this.obj.status === PaymentStatus.Unpaid
                      ? PaymentButtonWithState({
                          d3eState: this.d3eState,
                          _onPaymentButtonHandler: this.onPaymentButtonHandler,
                        })
                      : [],
                  ],
                })
              : [],
          ],
          className: "xb1e hc h",
          key: "0",
        }),
        ui.Column({
          children: [
            ui.Row({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    TextView({
                      data: "Invoice ID",
                      style: new ui.TextStyle({
                        fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                        fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                      }),
                      className: "x48a hc",
                      key: "0",
                    }),
                    TextView({
                      data: this.obj.title,
                      className: "hc",
                      key: "1",
                    }),
                  ],
                  className: "xfd4 hc",
                  key: "0",
                }),
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    TextView({
                      data: "Date",
                      style: new ui.TextStyle({
                        fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                        fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                      }),
                      className: "xc13 hc",
                      key: "0",
                    }),
                    TextView({
                      data: DateUtils.format(this.obj.invoiceDate),
                      className: "hc",
                      key: "1",
                    }),
                  ],
                  className: "xed2 hc",
                  key: "1",
                }),
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    TextView({
                      data: "Due Date",
                      style: new ui.TextStyle({
                        fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                        fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                      }),
                      className: "xe2c hc",
                      key: "0",
                    }),
                    TextView({
                      data: this.obj.dueDate.toString(),
                      className: "hc",
                      key: "1",
                    }),
                  ],
                  className: "xab hc",
                  key: "2",
                }),
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    TextView({
                      data: "Hospital Name",
                      style: new ui.TextStyle({
                        fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                        fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                      }),
                      className: "xb7",
                      key: "0",
                    }),
                    TextView({ data: this.obj.hospital.name, key: "1" }),
                  ],
                  className: "x31",
                  key: "3",
                }),
              ],
              className: "x12c hc h",
              key: "0",
            }),
            ui.Row({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    TextView({
                      data: "Payment Status",
                      style: new ui.TextStyle({
                        fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                        fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                      }),
                      className: "xe04 hc",
                      key: "0",
                    }),
                    TextView({
                      data: this.obj.status.name,
                      states: ui.joinStates(
                        {
                          "data-c0": this.obj.status === PaymentStatus.Paid,
                          "data-c1": this.obj.status === PaymentStatus.Unpaid,
                          "data-c2": this.obj.status === PaymentStatus.Fail,
                        },
                        {}
                      ),
                      style: new ui.TextStyle({
                        color:
                          this.obj.status === PaymentStatus.Paid
                            ? cStyle.c24
                            : this.obj.status === PaymentStatus.Unpaid
                            ? cStyle.c8
                            : this.obj.status === PaymentStatus.Fail
                            ? cStyle.c25
                            : cStyle.c8,
                      }),
                      className: "x28 hc",
                      key: "1",
                    }),
                  ],
                  className: "xa2 hc",
                  key: "0",
                }),
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    TextView({
                      data: "Total Amount",
                      style: new ui.TextStyle({
                        fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                        fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                      }),
                      className: "xfb7 hc",
                      key: "0",
                    }),
                    TextView({
                      data: "$" + this.obj.amount.toString(),
                      className: "hc",
                      key: "1",
                    }),
                  ],
                  className: "x574 hc",
                  key: "1",
                }),
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    TextView({
                      data: "Services Count",
                      style: new ui.TextStyle({
                        fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                        fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                      }),
                      className: "xc3",
                      key: "0",
                    }),
                    TextView({
                      data: this.obj.requestCount.toString(),
                      key: "1",
                    }),
                  ],
                  className: "x39",
                  key: "2",
                }),
              ],
              className: "x43f hc h",
              key: "1",
            }),
            ui.Column({
              children: [
                ui.Column({
                  children: [
                    ui.Table({
                      defaultColumnWidth: new ui.FlexColumnWidth(1),
                      columnWidths: {
                        0: new ui.FlexColumnWidth(1),
                        1: new ui.FlexColumnWidth(1),
                        2: new ui.FlexColumnWidth(1),
                        3: new ui.FlexColumnWidth(1),
                      },
                      children: [
                        ui.TableRow({
                          children: [
                            ui.TableCell({
                              verticalAlignment:
                                ui.TableCellVerticalAlignment.middle,
                              child: TextView({
                                data: "Request ID",
                                style: new ui.TextStyle({
                                  fontFamily:
                                    cStyle.tTextViewTableHeaderFontFamilyOn,
                                  color: cStyle.tTextViewTableHeaderColorOn,
                                  fontSize:
                                    cStyle.tTextViewTableHeaderFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewTableHeaderFontWeightOn,
                                }),
                                className: "hc",
                              }),
                              className: "hc",
                              key: "0",
                            }),
                            ui.TableCell({
                              verticalAlignment:
                                ui.TableCellVerticalAlignment.middle,
                              child: TextView({
                                data: "Created Date",
                                style: new ui.TextStyle({
                                  fontFamily:
                                    cStyle.tTextViewTableHeaderFontFamilyOn,
                                  color: cStyle.tTextViewTableHeaderColorOn,
                                  fontSize:
                                    cStyle.tTextViewTableHeaderFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewTableHeaderFontWeightOn,
                                }),
                                className: "hc",
                              }),
                              className: "hc",
                              key: "1",
                            }),
                            ui.TableCell({
                              verticalAlignment:
                                ui.TableCellVerticalAlignment.middle,
                              child: TextView({
                                data: "Created By",
                                style: new ui.TextStyle({
                                  fontFamily:
                                    cStyle.tTextViewTableHeaderFontFamilyOn,
                                  color: cStyle.tTextViewTableHeaderColorOn,
                                  fontSize:
                                    cStyle.tTextViewTableHeaderFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewTableHeaderFontWeightOn,
                                }),
                                className: "hc",
                              }),
                              className: "hc",
                              key: "2",
                            }),
                            ui.TableCell({
                              verticalAlignment:
                                ui.TableCellVerticalAlignment.middle,
                              child: TextView({
                                data: "Resolved Date",
                                style: new ui.TextStyle({
                                  fontFamily:
                                    cStyle.tTextViewTableHeaderFontFamilyOn,
                                  color: cStyle.tTextViewTableHeaderColorOn,
                                  fontSize:
                                    cStyle.tTextViewTableHeaderFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewTableHeaderFontWeightOn,
                                }),
                                className: "hc",
                              }),
                              className: "hc",
                              key: "3",
                            }),
                          ],
                          className: "x692 hc",
                          key: "0",
                        }),
                        this.query !== null && this.query.items.isNotEmpty
                          ? this.requestListByInvoice.expand((forItem) => [
                              ui.TableRow({
                                children: [
                                  ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data: forItem.searchId,
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                    key: "0",
                                  }),
                                  ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data: DateUtils.format(
                                        forItem.requestDate
                                      ),
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                    key: "1",
                                  }),
                                  ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data: forItem.createdBy.name,
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                    key: "2",
                                  }),
                                  ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data:
                                        DateUtils.format(forItem.requestDate) ??
                                        "Pending",
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                    key: "3",
                                  }),
                                ],
                                className: "xfee hc",
                                key: forItem?.ident,
                              }),
                            ])
                          : [],
                      ],
                      className: "x306 hc h",
                      key: "0",
                    }),
                    this.query !== null && this.query.items.isEmpty
                      ? ui.Column({
                          mainAxisAlignment: ui.MainAxisAlignment.center,
                          children: [
                            ui.AssetImage({
                              path: "images/emptyList.png",
                              width: 300,
                              height: 300,
                              className: "x9a hc vc",
                              key: "0",
                            }),
                            TextView({
                              data: "No Records",
                              style: new ui.TextStyle({
                                fontWeight: ui.FontWeight.bold,
                                fontSize: 16,
                              }),
                              className: "xe91 hc",
                              key: "1",
                            }),
                          ],
                          className: "x68 hc",
                        })
                      : [],
                    ui.Row({
                      mainAxisAlignment: ui.MainAxisAlignment.end,
                      children: [
                        PaginationView({
                          pageSize: this.request.pageSize,
                          total: this.query !== null ? this.query.count : 0,
                          offset: this.request.offset,
                          onChange: (pageSize, offset) => {
                            this.onPaginationViewChanged(
                              pageSize,
                              offset,
                              this.d3eState
                            );
                          },
                          states: ui.joinStates(
                            {
                              "data-visibility":
                                this.query !== null &&
                                this.query.items.isNotEmpty,
                            },
                            {}
                          ),
                          key: "0",
                        }),
                      ],
                      className: "x9c0 hc h",
                      key: "2",
                    }),
                  ],
                  className: "x16f hc h",
                  key: "0",
                }),
              ],
              className: "x163 hc h",
              key: "2",
            }),
          ],
          className: "xd8 hc h",
          key: "1",
        }),
      ],
      className: ui.join(this.props.className, "BillingDetailsView xf7 hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setRequest(
      new RequestsByInvoiceRequest({
        pageSize: 50,
        offset: 0,
        orderBy: "sortOrder",
        ascending: true,
        invoice: this.obj,
        hospital: this.obj.hospital,
      })
    );
  };
  public onBackButtonHandler = (d3eState: BillingDetailsViewRefs): void => {
    this.navigator.pushSystemAdminInvoiceList({
      admin: this.admin,
      user: this.user,
      target: "main",
      replace: false,
    });
  };
  public onPaginationViewChanged = (
    pageSize: number,
    offset: number,
    d3eState: BillingDetailsViewRefs
  ): void => {
    this.request.setPageSize(pageSize);

    this.request.setOffset(offset);

    this.setRequest(this.request.deepClone());
  };
  public onPaymentButtonHandler = async (
    d3eState: BillingDetailsViewRefs
  ): Promise<void> => {
    let link: string = await RPCServices.getPaymentService().createSessionLink(
      this.obj.invoiceId,
      this.obj.title,
      this.obj.amount
    );

    Browser.open(link);

    this.navigator.close();
  };
  public onDownloadButtonHandler = async (
    d3eState: BillingDetailsViewRefs
  ): Promise<void> => {
    let file: DFile = await RPCServices.getExportUtils().createInvoice(
      this.request
    );

    if (file !== null) {
      Browser.download(file);
    }
  };
  public onPrintButtonHandler = (d3eState: BillingDetailsViewRefs): void => {
    Browser.print();
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.query);

    super.dispose();
  }
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get downloadButton() {
    return this.d3eState.downloadButton;
  }
  public get paymentButton() {
    return this.d3eState.paymentButton;
  }
  public get printButton() {
    return this.d3eState.printButton;
  }
}
export default function BillingDetailsView(props: BillingDetailsViewProps) {
  return React.createElement(_BillingDetailsViewState, {
    ..._BillingDetailsViewState.defaultProps,
    ...props,
  });
}
